/**
 * DashboardPage Component
 * 대시보드 메인 레이아웃 페이지
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../../layout/Sidebar/Sidebar';
import Header from '../../layout/Header/Header';
import Footer from '@/features/dashboard/components/layout/Footer/Footer';
import { getItem, setItem } from '@/shared/utils/storage';
import { authApi } from '@/features/auth/api/authApi';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { clearFilters } from '@/shared/utils/filterStorage';
import { getPageLabelByType } from '@/features/dashboard/config/pageLabels.config';
import { getFilterKeysByTabId } from '@/features/dashboard/config/filterKeys.config';
import type { Tab } from '@/features/dashboard/types/dashboard.types';
import { useActiveMaintenance } from '@/features/system/maintenance/hooks/useMaintenanceData';
import MaintenanceScreen from '@/features/system/maintenance/components/MaintenanceScreen';
import { NoticePopupModal } from '@/features/notice/components/modals/NoticePopupModal';
import { noticeApi } from '@/features/notice/api/noticeApi';
import { Notice, noticeListFromDto } from '@/features/notice/types/notice.types';
import { getHiddenNoticeIdsForToday, saveHiddenNoticeForToday } from '@/features/dashboard/utils/noticeStorageUtils';
import DashboardTabBar from './DashboardTabBar';
import DashboardTabRouter from './DashboardTabRouter';
import '@/shared/styles/search-form.css';
import '../../../styles/DashboardPage.css';

const DashboardPage: React.FC = () => {
    const location = useLocation();

    const [currentPage, setCurrentPage] = useState<string>(() => getItem<string>('CURRENT_PAGE') || 'dashboard');
    const [tabs, setTabs] = useState<Tab[]>(() => {
        const savedTabs = getItem<Tab[]>('DASHBOARD_TABS');
        return savedTabs && savedTabs.length > 0
            ? savedTabs
            : [{ id: 'dashboard', label: '대시보드', page: 'dashboard' }];
    });
    const [activeTabId, setActiveTabId] = useState<string>(
        () => getItem<string>('ACTIVE_TAB_ID') || 'dashboard',
    );
    const [selectedRiderId, setSelectedRiderId] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState({ userName: '', userEmail: '', userType: 'HEADQUARTERS' });
    const [isUserInfoLoading, setIsUserInfoLoading] = useState(true);
    const { showToast } = useToast();

    const { activeMaintenance, isMaintenance } = useActiveMaintenance();

    const [maintenanceAcknowledged, setMaintenanceAcknowledged] = useState(() => {
        const acknowledged = sessionStorage.getItem('maintenance_acknowledged');
        const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
        if (activeMaintenance && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
            sessionStorage.removeItem('maintenance_acknowledged');
            return false;
        }
        return acknowledged === 'true';
    });

    const [popupNotices, setPopupNotices] = useState<Notice[]>([]);
    const [visibleNoticeIds, setVisibleNoticeIds] = useState<Set<number>>(new Set());
    const [noticeZIndexMap, setNoticeZIndexMap] = useState<Map<number, number>>(new Map());
    const [maxZIndex, setMaxZIndex] = useState(10000);

    const currentUserType = getItem<string>('USER_TYPE') || userInfo.userType;
    const allowedPagesDuringMaintenance = ['dashboard'];
    const isSupplierUnderMaintenance =
        currentUserType === 'SUPPLIER' &&
        isMaintenance &&
        !maintenanceAcknowledged &&
        !allowedPagesDuringMaintenance.includes(currentPage);

    useEffect(() => {
        if (!isMaintenance) {
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            setMaintenanceAcknowledged(false);
        } else if (activeMaintenance) {
            const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
            if (acknowledgedSeqNo && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
                sessionStorage.removeItem('maintenance_acknowledged');
                sessionStorage.removeItem('maintenance_acknowledged_seqno');
                setMaintenanceAcknowledged(false);
            }
        }
    }, [isMaintenance, activeMaintenance]);

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userId = getItem<string>('USER_ID');
                if (!userId) {
                    console.error('로그인 정보가 없습니다.');
                    showToast('로그인 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
                    setIsUserInfoLoading(false);
                    return;
                }
                const response = await authApi.getUserInfo(userId);
                const userType = getItem<string>('USER_TYPE') || 'HEADQUARTERS';
                setUserInfo({ userName: response.userName, userEmail: response.emailInfo, userType });
                setIsUserInfoLoading(false);
            } catch (error) {
                console.error('사용자 정보 조회 실패:', error);
                showToast('사용자 정보를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                const userName = getItem<string>('USER_NAME') || '';
                const userType = getItem<string>('USER_TYPE') || 'HEADQUARTERS';
                setUserInfo({ userName, userEmail: '', userType });
                setIsUserInfoLoading(false);
            }
        };
        fetchUserInfo();
    }, [showToast]);

    useEffect(() => {
        const fetchPopupNotices = async () => {
            const userType = getItem<string>('USER_TYPE');
            if (userType !== 'SUPPLIER') return;
            try {
                const response = await noticeApi.getUnreadPopupNotices();
                const allNotices = noticeListFromDto(response);
                const hiddenIds = getHiddenNoticeIdsForToday();
                const notices = allNotices.filter((n) => !hiddenIds.includes(n.noticeId));
                if (notices.length > 0) {
                    setPopupNotices(notices);
                    setVisibleNoticeIds(new Set(notices.map((n) => n.noticeId)));
                    const initialZIndexMap = new Map<number, number>();
                    notices.forEach((n, idx) => { initialZIndexMap.set(n.noticeId, 10000 + idx); });
                    setNoticeZIndexMap(initialZIndexMap);
                    setMaxZIndex(10000 + notices.length);
                }
            } catch (error) {
                console.error('팝업 공지 조회 실패:', error);
            }
        };
        if (!isUserInfoLoading) fetchPopupNotices();
    }, [isUserInfoLoading]);

    useEffect(() => {
        if (location.state) {
            const { page, riderId } = location.state as { page?: string; riderId?: string };
            if (page === 'rider_detail' && riderId) {
                setCurrentPage('rider_detail');
                setSelectedRiderId(riderId);
            }
        }
    }, [location.state]);

    useEffect(() => {
        setItem('DASHBOARD_TABS', tabs);
        setItem('ACTIVE_TAB_ID', activeTabId);
        setItem('CURRENT_PAGE', currentPage);
    }, [tabs, activeTabId, currentPage]);

    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            const userId = getItem<string>('USER_ID');
            if (userId && location.pathname === '/dashboard') {
                const state = event.state as { page?: string } | null;
                if (state?.page) {
                    const tab = tabs.find((t) => t.page === state.page);
                    if (tab) {
                        setActiveTabId(tab.id);
                        setCurrentPage(tab.page);
                        return;
                    }
                }
                const dashboardTab = tabs.find((tab) => tab.id === 'dashboard');
                if (!dashboardTab) {
                    setTabs([{ id: 'dashboard', label: '대시보드', page: 'dashboard' }, ...tabs]);
                }
                setActiveTabId('dashboard');
                setCurrentPage('dashboard');
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [tabs, location.pathname]);

    const getPageLabel = useCallback(
        (page: string): string => {
            const isEditMode =
                page === 'common_code_add'
                    ? getItem<string>('COMMON_CODE_EDIT_MODE') === 'true'
                    : undefined;
            return getPageLabelByType(page, userInfo.userType, isEditMode);
        },
        [userInfo.userType],
    );

    const handlePageChange = useCallback(
        (page: string) => {
            setTabs((prevTabs) => {
                const existingTab = prevTabs.find((tab) => tab.page === page);
                if (existingTab) {
                    const updatedLabel = getPageLabel(page);
                    if (existingTab.label !== updatedLabel) {
                        return prevTabs.map((tab) =>
                            tab.id === existingTab.id ? { ...tab, label: updatedLabel } : tab,
                        );
                    }
                    setActiveTabId(existingTab.id);
                    setCurrentPage(page);
                    return prevTabs;
                }
                const newTab: Tab = { id: page, label: getPageLabel(page), page };
                setActiveTabId(page);
                setCurrentPage(page);
                return [...prevTabs, newTab];
            });
            window.history.pushState({ page }, '', '/dashboard');
        },
        [getPageLabel],
    );

    const handleTabClick = (tabId: string) => {
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) {
            setActiveTabId(tabId);
            setCurrentPage(tab.page);
            window.history.pushState({ page: tab.page }, '', '/dashboard');
        }
    };

    const handleCloseTab = useCallback(
        (tabId: string, e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
            if (tabIndex === -1 || tabId === 'dashboard') return;

            const filterKeys = getFilterKeysByTabId(tabId);
            if (filterKeys) filterKeys.forEach((key) => clearFilters(key));

            const newTabs = tabs.filter((tab) => tab.id !== tabId);
            setTabs(newTabs);

            if (activeTabId === tabId) {
                if (tabIndex > 0) {
                    const prevTab = newTabs[tabIndex - 1];
                    setActiveTabId(prevTab.id);
                    setCurrentPage(prevTab.page);
                } else if (newTabs.length > 0) {
                    const nextTab = newTabs[0];
                    setActiveTabId(nextTab.id);
                    setCurrentPage(nextTab.page);
                }
            }
        },
        [tabs, activeTabId],
    );

    const handleCloseAllTabs = () => {
        tabs.forEach((tab) => {
            const filterKeys = getFilterKeysByTabId(tab.id);
            if (filterKeys) filterKeys.forEach((key) => clearFilters(key));
        });
        const dashboardTab = tabs.find((tab) => tab.id === 'dashboard');
        if (dashboardTab) {
            setTabs([dashboardTab]);
            setActiveTabId('dashboard');
            setCurrentPage('dashboard');
        }
    };

    useEffect(() => {
        const handleDashboardNavigate = (event: Event) => {
            const customEvent = event as CustomEvent<{ page?: string }>;
            if (customEvent.detail?.page) handlePageChange(customEvent.detail.page);
        };
        const handleCloseTabEvent = (event: Event) => {
            const customEvent = event as CustomEvent<{ tabId?: string }>;
            if (customEvent.detail?.tabId) handleCloseTab(customEvent.detail.tabId);
        };
        window.addEventListener('dashboard:navigate', handleDashboardNavigate);
        window.addEventListener('dashboard:close-tab', handleCloseTabEvent);
        return () => {
            window.removeEventListener('dashboard:navigate', handleDashboardNavigate);
            window.removeEventListener('dashboard:close-tab', handleCloseTabEvent);
        };
    }, [handlePageChange, handleCloseTab]);

    const handleMaintenanceAcknowledge = () => {
        if (activeMaintenance) {
            sessionStorage.setItem('maintenance_acknowledged', 'true');
            sessionStorage.setItem('maintenance_acknowledged_seqno', String(activeMaintenance.seqNo));
            setMaintenanceAcknowledged(true);
        }
    };

    const handleHideForToday = useCallback((noticeId: number) => {
        saveHiddenNoticeForToday(noticeId);
    }, []);

    const handleBringToFront = useCallback((noticeId: number) => {
        setMaxZIndex((prev) => {
            const newMaxZIndex = prev + 1;
            setNoticeZIndexMap((prevMap) => {
                const newMap = new Map(prevMap);
                newMap.set(noticeId, newMaxZIndex);
                return newMap;
            });
            return newMaxZIndex;
        });
    }, []);

    const handleCloseNoticePopup = useCallback((noticeId: number) => {
        setVisibleNoticeIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(noticeId);
            return newSet;
        });
    }, []);

    return (
        <div className="dashboard-layout">
            <Sidebar onPageChange={handlePageChange} currentPage={currentPage} />

            {popupNotices.map((notice, index) =>
                visibleNoticeIds.has(notice.noticeId) ? (
                    <NoticePopupModal
                        key={notice.noticeId}
                        notice={notice}
                        index={index}
                        zIndex={noticeZIndexMap.get(notice.noticeId) || 10000 + index}
                        onClose={() => handleCloseNoticePopup(notice.noticeId)}
                        onHideForToday={handleHideForToday}
                        onBringToFront={handleBringToFront}
                    />
                ) : null,
            )}

            {currentUserType === 'SUPPLIER' && isMaintenance && activeMaintenance && !maintenanceAcknowledged ? (
                <MaintenanceScreen
                    maintenance={activeMaintenance}
                    showDashboardButton={true}
                    onDashboardClick={handleMaintenanceAcknowledge}
                />
            ) : (
                <div className="dashboard-main">
                    <Header
                        userName={isUserInfoLoading ? undefined : userInfo.userName}
                        userEmail={isUserInfoLoading ? undefined : userInfo.userEmail}
                        userType={userInfo.userType}
                    />

                    <DashboardTabBar
                        tabs={tabs}
                        activeTabId={activeTabId}
                        currentUserType={currentUserType}
                        isMaintenance={isMaintenance}
                        getPageLabel={getPageLabel}
                        onTabClick={handleTabClick}
                        onCloseTab={handleCloseTab}
                        onCloseAllTabs={handleCloseAllTabs}
                    />

                    <div className="dashboard-content">
                        {isSupplierUnderMaintenance && activeMaintenance ? (
                            <MaintenanceScreen
                                maintenance={activeMaintenance}
                                showDashboardButton={true}
                                onDashboardClick={handleMaintenanceAcknowledge}
                            />
                        ) : (
                            <DashboardTabRouter
                                currentPage={currentPage}
                                userType={userInfo.userType}
                                selectedRiderId={selectedRiderId}
                                handlePageChange={handlePageChange}
                                showToast={showToast}
                            />
                        )}
                    </div>
                    <Footer />
                </div>
            )}
        </div>
    );
};

export default DashboardPage;
